import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, Text } from '@rsos/components/capstone/base';
import { setSelectedCallerID } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import {
  setIsEditing,
  useActivateTransportMutation,
  parseTransportVerbatim,
  type EtlTransportResponse,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import { trackEditAirAmbulanceRequest } from '../../../gainsightTrackings/airAmbulanceTrackings';
import { Loader } from '../AirAmbulanceForm/AirAmbulanceFormStandalone.styles';
import { LAUNCH_TYPES } from '../AirAmbulanceForm/constants';
import AirAmbulanceEtlCancelModal from './AirAmbulanceEtlCancelModal';

interface AirAmbulanceEtlStatusControlsProps {
  transport: EtlTransportResponse;
}

/**
 * Renders the set of action buttons on the bottom of the status page.
 */
function AirAmbulanceEtlStatusControls({
  transport,
}: AirAmbulanceEtlStatusControlsProps): JSX.Element {
  const dispatch = useDispatch();
  const [activateTransport] = useActivateTransportMutation();
  const [isActivating, setIsActivating] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const verbatim = parseTransportVerbatim(transport.verbatim_message);
  const tripLeg = verbatim?.tripLeg?.[0];
  const isStandby = Boolean(tripLeg?.airDetails?.idenStandBy);

  const isCancelled = transport.statuses.some(status => {
    const vm = status.verbatim_message;
    const statusType = vm?.update?.status?.type;
    return (
      statusType === 'cancelPending' ||
      statusType === 'cancelled' ||
      statusType === 'cancelCompleted'
    );
  });

  const hasCallerId = Boolean(transport.caller_id);

  const handleViewRelatedCall = () => {
    if (transport.caller_id) {
      dispatch(setSelectedCallerID(transport.caller_id));
    }
  };

  const handleEditRequest = () => {
    trackEditAirAmbulanceRequest({
      requestId: transport.gmr_transport_id,
    });
    dispatch(setIsEditing(true));
  };

  const handleActivateStandby = async () => {
    setIsActivating(true);
    try {
      await activateTransport({ transportId: transport.id }).unwrap();
    } catch (error) {
      // eslint-disable-next-line no-console -- track errors in sentry
      console.error('Activate standby error:', error);
    } finally {
      setIsActivating(false);
    }
  };

  const cancelledTitle = isCancelled
    ? 'This request has been cancelled'
    : undefined;

  return (
    <>
      <Box mx="8px" mb={2}>
        <Box mb={2}>
          <Button
            type="button"
            grow
            btnType="primary"
            onClick={handleViewRelatedCall}
            disabled={!hasCallerId || isCancelled}
            title={!hasCallerId ? undefined : cancelledTitle}
          >
            View Related Call/Event
          </Button>
          {!hasCallerId && (
            <Text
              size="xsmall"
              color="secondaryText"
              style={{ marginTop: '4px' }}
            >
              This request is not associated with a call
            </Text>
          )}
        </Box>

        <Box mb={2}>
          <Button
            grow
            btnType="tertiary"
            type="button"
            onClick={handleEditRequest}
            disabled={isCancelled}
            title={cancelledTitle}
          >
            Edit Request
          </Button>
        </Box>

        <Box mb={2}>
          <Button
            grow
            btnType="warning_tertiary"
            type="button"
            onClick={() => setShowCancelModal(true)}
            disabled={isCancelled}
            title={cancelledTitle}
          >
            Cancel Request
          </Button>
        </Box>

        {isStandby && (
          <Box mb={2}>
            <Button
              grow
              btnType="primary"
              type="button"
              onClick={handleActivateStandby}
              disabled={isActivating || isCancelled}
              title={cancelledTitle}
            >
              {isActivating ? <Loader /> : `Activate ${LAUNCH_TYPES.STANDBY}`}
            </Button>
          </Box>
        )}
      </Box>

      <AirAmbulanceEtlCancelModal
        transport={transport}
        showModal={showCancelModal}
        onClose={() => setShowCancelModal(false)}
      />
    </>
  );
}

export default AirAmbulanceEtlStatusControls;
